import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CohortData } from "@/types/cohort.types";
import { Skeleton } from "@/components/ui/skeleton";

interface CohortRetentionTableProps {
  cohorts: CohortData[];
  isLoading?: boolean;
}

export const CohortRetentionTable = ({ cohorts, isLoading }: CohortRetentionTableProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Retention Heatmap</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const getRetentionColor = (rate: number): string => {
    if (rate >= 75) return "bg-green-500/20 text-green-700 dark:text-green-400";
    if (rate >= 50) return "bg-lime-500/20 text-lime-700 dark:text-lime-400";
    if (rate >= 25) return "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400";
    if (rate >= 10) return "bg-orange-500/20 text-orange-700 dark:text-orange-400";
    return "bg-red-500/20 text-red-700 dark:text-red-400";
  };

  const maxPeriods = Math.max(...cohorts.map(c => c.retention.length));

  return (
    <Card>
      <CardHeader>
        <CardTitle>📊 Retention Heatmap</CardTitle>
        <CardDescription>
          Wie viele User kehren nach ihrer ersten Interaktion zurück?
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-3 font-semibold">Kohorte</th>
                <th className="text-right p-3 font-semibold">Größe</th>
                {Array.from({ length: maxPeriods }, (_, i) => (
                  <th key={i} className="text-center p-3 font-semibold">
                    W{i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohorts.map((cohort) => (
                <tr key={cohort.cohort} className="border-b border-border/50 hover:bg-muted/50">
                  <td className="p-3 font-medium">{cohort.cohort}</td>
                  <td className="text-right p-3 text-muted-foreground">{cohort.cohortSize}</td>
                  {Array.from({ length: maxPeriods }, (_, i) => {
                    const retention = cohort.retention.find(r => r.period === i);
                    
                    if (!retention || i > 0 && !cohort.retention.find(r => r.period === i - 1)) {
                      return (
                        <td key={i} className="text-center p-3 text-muted-foreground">
                          -
                        </td>
                      );
                    }

                    return (
                      <td key={i} className="p-2">
                        <div
                          className={`rounded px-2 py-1 text-center font-medium ${getRetentionColor(retention.retentionRate)}`}
                        >
                          {retention.retentionRate.toFixed(0)}%
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          
          {cohorts.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Keine Kohortendaten verfügbar
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500/20" />
            <span>75%+</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-lime-500/20" />
            <span>50-75%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-500/20" />
            <span>25-50%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-500/20" />
            <span>10-25%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500/20" />
            <span>&lt;10%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
